import { gql } from '@reverbdotcom/commons/src/gql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';

/**
 * Exported so that any call site marking stream items read can refresh the bell
 * via `refetchQueries`. Prefer the hooks in `notifications_read_state`, which
 * already do this.
 */
export const NOTIFICATIONS_BELL_COUNT_QUERY = gql(`
  query NotificationsBellCount {
    notificationCounts {
      total
      totalLabel
    }
  }
`);

export interface NotificationsBellCount {
  /**
   * Total badge count for the bell, summed server-side across the three
   * notification surfaces. `0` until the first response lands, and `0` if that
   * first request fails.
   */
  count: number;
  label: string;
  loading: boolean;
}

/**
 * Owns the treatment bell's badge count, read from CoreQL.
 *
 * Only mount this on the treatment branch of the notifications-center
 * experiment — control users continue to read their count from `/my_counts`,
 * and mounting this hook for them would issue a `NotificationsBellCount`
 * request they never use.
 *
 * Read once per page, like the panel's stream. A badge that refreshed on its own
 * would count notifications the list underneath it can't show. The mark-read
 * mutations in `notifications_read_state` step it down.
 */
export function useNotificationsBellCount(): NotificationsBellCount {
  const { data, loading } = useQuery(NOTIFICATIONS_BELL_COUNT_QUERY);

  return {
    // Null on a partial success, so the hop is guarded.
    count: data?.notificationCounts?.total ?? 0,
    label: data?.notificationCounts?.totalLabel ?? '',
    loading,
  };
}
